import React, { useState, useEffect } from 'react'
import Head from 'next/head'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { FaChevronDown } from 'react-icons/fa'

export const DarkFaq = ({
  questionsAnswers,
  showSearch = true,
  title = "Frequently Asked Questions",
  subtitle = "",
  // false when the page already has the FAQ in its own JSON-LD graph (the
  // UAE / Malaysia pages, utils/market-schema.js faqNode).
  jsonLd = true,
}) => {
  const [filteredFaqs, setFilteredFaqs] = useState(questionsAnswers)
  const [searchText, setSearchText] = useState("")
  const [expandedFaq, setExpandedFaq] = useState(null)

  useEffect(() => {
    if (searchText.length > 2 && showSearch) {
      const filtered = questionsAnswers.filter(({ question, answer }) =>
        (question + answer).toLowerCase().includes(searchText.toLowerCase())
      )
      setFilteredFaqs(filtered)
    } else {
      setFilteredFaqs(questionsAnswers)
    }
  }, [searchText, questionsAnswers, showSearch])

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index)
  }

  const generateFaqSchema = () => {
    // Always built from the full, unfiltered list - otherwise the emitted
    // JSON-LD mutates as the visitor types in the search box.
    const faqEntities = questionsAnswers.slice(0, 10).map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqEntities
    }
  }

  return (
    <>
      {jsonLd &&
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(generateFaqSchema())
          }}
        />
      </Head>
      }

      <div className="py-8 px-2 md:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
              <span className="text-sm">❓</span>
              <span className="text-xs font-medium text-white/70">FAQs</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">{title}</h2>
            {subtitle && (
              <p className="text-sm text-white/50 mt-2">{subtitle}</p>
            )}
          </div>

          {/* Search */}
          {showSearch && (
            <div className="relative max-w-lg mx-auto mb-8">
              <input
                id="faq-search"
                type="search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search FAQs..."
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 focus:ring-2 ring-orange-400/50 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-orange-400/50"
              />
              <div className="absolute left-3 top-3.5 text-white/30">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M10 18a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </div>
            </div>
          )}

          {/* FAQ Items */}
          <div className="space-y-3">
            {filteredFaqs.map((item, index) => (
              <div
                key={index}
                className={`rounded-2xl overflow-hidden transition-all duration-300 border ${
                  expandedFaq === index 
                    ? 'bg-white/10 border-orange-400/30' 
                    : 'bg-white/5 border-white/10 hover:bg-white/[0.07] hover:border-white/20'
                }`}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-5 py-4 text-left flex justify-between items-center gap-3"
                >
                  <span className="text-sm font-semibold text-white/90">{item.question}</span>
                  <motion.span
                    animate={{ rotate: expandedFaq === index ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="text-orange-400 shrink-0"
                  >
                    <FaChevronDown className="text-xs" />
                  </motion.span>
                </button>

                <motion.div
                  initial={false}
                  animate={{
                    height: expandedFaq === index ? "auto" : 0,
                    opacity: expandedFaq === index ? 1 : 0
                  }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="text-sm px-5 pb-4 text-white/50 leading-relaxed">
                    {item.answer}
                    {item.link && (
                      <Link
                        href={item.link.href}
                        className="block mt-2 text-orange-400 hover:text-orange-300 font-medium underline underline-offset-2"
                      >
                        {item.link.text}
                      </Link>
                    )}
                  </div>
                </motion.div>
              </div>
            ))}

            {filteredFaqs.length === 0 && (
              <p className="text-center text-sm text-white/30">No matching FAQs found.</p>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
